/**
 * ListItemRow Component
 *
 * Renders a single item row in a list detail view.
 * Shows photo/cover, name, restaurant name, note preview,
 * and optional edit/remove actions.
 */

import { formatRelativeTime } from '@/utils/format-time';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import type { ListItem } from '../types';

interface ListItemRowProps {
  item: ListItem;
  canEdit: boolean;
  onRemove?: () => void;
  onEditNote?: () => void;
}

export function ListItemRow({ item, canEdit, onRemove, onEditNote }: ListItemRowProps) {
  const coverUrl = item.coverPhotoUrl ? imageUrl(item.coverPhotoUrl, 'thumbnail') : null;
  const displayName = item.menuItemName || item.businessName || 'Unknown item';
  const subtitle = item.itemType === 'menu_item' && item.businessName
    ? item.businessName
    : item.itemType === 'business'
      ? 'Restaurant'
      : undefined;

  const handlePress = () => {
    if (item.itemType === 'menu_item') {
      router.push({
        pathname: '/(root)/menu-item/[id]',
        params: { id: item.itemId },
      });
    } else {
      router.push({
        pathname: '/(root)/business/[id]',
        params: { id: item.itemId },
      });
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      className="flex-row items-center p-3 rounded-xl mb-2 bg-surface border border-border"
      accessibilityRole="button"
      accessibilityLabel={`${displayName}${subtitle ? ` at ${subtitle}` : ''}`}
    >
      {/* Thumbnail */}
      {coverUrl ? (
        <Image
          source={{ uri: coverUrl }}
          className="rounded-lg bg-surface-secondary"
          style={{ width: 56, height: 56 }}
          resizeMode="cover"
          accessibilityLabel={`Photo of ${displayName}`}
        />
      ) : (
        <View
          className="rounded-lg items-center place-self-start justify-center bg-surface-secondary"
          style={{ width: 56, height: 56 }}
        >
          <Ionicons
            name={item.itemType === 'menu_item' ? 'restaurant-outline' : 'storefront-outline'}
            size={24}
            className="text-muted-foreground"
          />
        </View>
      )}

      {/* Content */}
      <View className="flex-1 ml-3">
        <Text
          className="font-semibold text-base text-foreground"
          numberOfLines={1}
        >
          {displayName}
        </Text>
        {subtitle && (
          <Text className="text-sm mt-0.5 text-muted-foreground" numberOfLines={1}>
            {subtitle}
          </Text>
        )}
        {item.note && (
          <Text className="text-xs mt-1 text-muted-foreground italic" numberOfLines={1}>
            {item.note}
          </Text>
        )}
        {(item.addedByName || item.createdAt) && (
          <View className="flex-row items-center mt-1">
            {item.addedByName && (
              <>
                <Text className="text-xs text-muted-foreground">Added by </Text>
                <Pressable
                  onPress={(e) => {
                    e.stopPropagation();
                    router.push(`/(root)/profile/${item.addedBy}`);
                  }}
                  hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
                  accessibilityRole="link"
                  accessibilityLabel={`View ${item.addedByName}'s profile`}
                >
                  <Text className="text-xs text-primary">{item.addedByName}</Text>
                </Pressable>
              </>
            )}
            {item.addedByName && item.createdAt && (
              <Text className="text-xs text-muted-foreground"> {'\u2014'} </Text>
            )}
            {item.createdAt && (
              <Text className="text-xs text-muted-foreground">
                {formatRelativeTime(item.createdAt)}
              </Text>
            )}
          </View>
        )}
      </View>

      {/* Actions */}
      {canEdit && (
        <View className="flex-row items-center ml-2" style={{ gap: 8 }}>
          {onEditNote && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation?.();
                onEditNote();
              }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Edit note"
            >
              <Ionicons name="pencil-outline" size={18} className="text-muted-foreground" />
            </Pressable>
          )}
          {onRemove && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation?.();
                onRemove();
              }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Remove from list"
            >
              <Ionicons name="trash-outline" size={18} className="text-destructive" />
            </Pressable>
          )}
        </View>
      )}
    </Pressable>
  );
}
